// lazyPage — React.lazy that survives a deploy.
//
// Vite names every page chunk by content hash. When a new build is published,
// a tab that still holds the OLD index.html (or one that loaded index mid-deploy)
// asks for a chunk that no longer exists and React throws
// "Failed to fetch dynamically imported module". Before this helper the error
// boundary showed that line and waited for a human to press Reload; a hard
// refresh during the deploy window fails the same way.
//
// Now: the first such failure reloads the page once (a reload fetches the new
// index and the chunks it names). A guard in sessionStorage stops a reload
// loop: if the reload happened less than a minute ago the error is re-thrown
// and the boundary shows it as before. Offline tabs are not reloaded, a reload
// cannot help them.

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

export const CHUNK_RELOAD_KEY = 'wolf.chunkReloadAt';
export const CHUNK_RELOAD_MIN_GAP_MS = 60_000;

const CHUNK_ERROR_RE = /dynamically imported module|Loading chunk|Importing a module script failed|error loading dynamically imported module/i;

export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return CHUNK_ERROR_RE.test(msg);
}

type Deps = {
  now?: () => number;
  online?: () => boolean;
  reload?: () => void;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
};

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null; } catch { return null; }
}

/** Decide whether a failed import should reload the page; records the reload when it does. */
export function shouldReloadForChunkError(err: unknown, deps: Deps = {}): boolean {
  if (!isChunkLoadError(err)) return false;
  const online = deps.online ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  if (!online()) return false;
  const storage = deps.storage === undefined ? defaultStorage() : deps.storage;
  const now = deps.now ?? Date.now;
  let last = 0;
  try { last = Number(storage?.getItem(CHUNK_RELOAD_KEY) || 0) || 0; } catch { last = 0; }
  if (now() - last < CHUNK_RELOAD_MIN_GAP_MS) return false;
  try { storage?.setItem(CHUNK_RELOAD_KEY, String(now())); } catch { /* storage blocked: still reload once per page life */ }
  return true;
}

export function loadWithReload<T>(loader: () => Promise<T>, deps: Deps = {}): Promise<T> {
  return loader().catch((err: unknown) => {
    if (shouldReloadForChunkError(err, deps)) {
      (deps.reload ?? (() => window.location.reload()))();
      // The page is going away; never resolve so Suspense keeps its fallback
      // instead of flashing the error card for the last frames.
      return new Promise<T>(() => {});
    }
    throw err;
  });
}

export function lazyPage<T extends ComponentType<any>>(loader: () => Promise<{ default: T }>): LazyExoticComponent<T> {
  return lazy(() => loadWithReload(loader));
}
